import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useLanguage } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lock, Loader2, CheckCircle, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const { t, language, toggleLanguage } = useLanguage();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError(t("الرابط غير صالح", "Invalid link"));
  }, [token]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError(t("كلمة المرور 8 أحرف على الأقل", "Password must be 8+ characters")); return; }
    if (pw !== pw2) { setError(t("كلمتا المرور غير متطابقتين", "Passwords do not match")); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success(t("تم إعادة تعيين كلمة المرور", "Password reset successful"));
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(err.response?.data?.detail || t("فشل إعادة التعيين", "Reset failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-yellow-500 rounded-2xl flex items-center justify-center"><span className="text-black font-bold text-xl">س</span></div>
          </Link>
          <button onClick={toggleLanguage} className="text-xs text-slate-400 hover:text-yellow-500 absolute top-4 left-4">{language === "ar" ? "EN" : "AR"}</button>
        </div>

        <div className="glass-card rounded-3xl p-8" data-testid="reset-password-card">
          {done ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="text-green-400" size={32} />
              </div>
              <h1 className="text-2xl font-bold text-white">{t("تم بنجاح", "Success!")}</h1>
              <p className="text-slate-300">{t("جاري تحويلك إلى صفحة تسجيل الدخول...", "Redirecting to login...")}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <h1 className="text-2xl font-bold text-luxury mb-2">{t("كلمة مرور جديدة", "New password")}</h1>
                <p className="text-slate-400 text-sm">{t("اختر كلمة مرور قوية لحسابك.", "Choose a strong password for your account.")}</p>
              </div>
              {error && <p className="text-red-400 text-sm bg-red-500/10 p-2 rounded-lg">{error}</p>}
              <div>
                <label className="text-sm text-slate-300 block mb-2">{t("كلمة المرور الجديدة", "New password")}</label>
                <div className="relative">
                  <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-yellow-500" size={16} />
                  <Input
                    type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} required
                    minLength={8} className="bg-slate-900 border-slate-700 text-white pr-10 pl-10 py-6 rounded-xl" data-testid="new-pw-input"
                  />
                  <button type="button" onClick={() => setShow(!show)} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-yellow-500">
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-300 block mb-2">{t("تأكيد كلمة المرور", "Confirm password")}</label>
                <div className="relative">
                  <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-yellow-500" size={16} />
                  <Input
                    type={show ? "text" : "password"} value={pw2} onChange={(e) => setPw2(e.target.value)} required
                    minLength={8} className="bg-slate-900 border-slate-700 text-white pr-10 pl-3 py-6 rounded-xl" data-testid="confirm-pw-input"
                  />
                </div>
              </div>
              <Button type="submit" disabled={loading || !token} className="btn-primary w-full h-12 rounded-xl" data-testid="reset-submit-btn">
                {loading ? <Loader2 className="animate-spin" size={18} /> : t("إعادة تعيين", "Reset Password")}
              </Button>
              <Link to="/login" className="flex items-center justify-center gap-2 text-sm text-yellow-500 hover:underline">
                <ArrowRight size={14} />
                {t("العودة لتسجيل الدخول", "Back to login")}
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
